// Global
import React from 'react';
import { tv } from 'tailwind-variants';

// Local
import { Content, PageSpecific } from '.generated/Feature.CW.model';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { Field, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import ActionCardSection from 'helpers/ActionCardSection';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import useIsEditing from 'lib/hooks/useIsEditing';
export type ConditionalHeaderProps = Content.ConditionHeader;
export type pageSpecific = PageSpecific.BasePage & {
  fields: {
    PageType?: {
      fields?: {
        Value?: Field<string>;
      };
    };
  };
};

const ConditionalHeader = (props: ConditionalHeaderProps): JSX.Element => {
  const { getDictionaryValue } = useDictionary();
  const { actionCardsList } = props?.fields || {};
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext;
  const hideTranslationLink = contextWithSettings?.route?.fields
    ?.hideTranslationHeaderLink as Field<boolean>;
  //const languageCTA = contextWithSettings?.siteSettings?.languageCTA;
  const { base, HeadingWrapper, CategoryLabel, Title, Description, ActionCardWrapper } =
    TAILWIND_VARIANTS();
  const language = contextWithSettings?.language;
  const languages = contextWithSettings?.languages || [];
  const pagePath = contextWithSettings?.itemPath || '';
  const pageInfo = sitecoreContext?.route as pageSpecific;
  const isEditing = useIsEditing();

  // Check if both en and es-ES exist
  const hasEN = languages.some((lang) => lang.isoCode === 'en');
  const hasES = languages.some((lang) => lang.isoCode === 'es-ES');

  // Determine alternate language link and text
  let alternateLanguageText = '';
  const alternateLanguageHref = pagePath;
  let locale = '';

  // this logic will be changed after url setting is done
  if (hasEN && hasES) {
    if (language === 'en') {
      alternateLanguageText = getDictionaryValue('LanguageCTAText');
      locale = `es-ES`;
    } else if (language === 'es-ES') {
      alternateLanguageText = getDictionaryValue('LanguageCTAText');
      locale = `en`;
    }
  }

  // Construct a new LinkField for the language switch link
  const customLanguageLink = {
    value: {
      text: alternateLanguageText,
      href: alternateLanguageHref,
      title: alternateLanguageText,
    },
  };

  return (
    <section data-component="authorable/shared/content/conditionHeader">
      <div className={base()}>
        <div className={HeadingWrapper()}>
          <div className="lg:min-w-[400px] min-w-[200px] w-full">
            <div className="flex justify-between">
              <div>
                {pageInfo?.fields?.PageType?.fields?.Value?.value && (
                  <span className={CategoryLabel()}>
                    {pageInfo?.fields?.PageType?.fields?.Value?.value}
                  </span>
                )}
                {(pageInfo?.fields?.Title?.value !== '' ||
                  props?.fields?.title?.value !== '' ||
                  isEditing) && (
                  <h1 className={Title()}>
                    <PlainTextWrapper
                      field={
                        props?.fields?.title?.value !== ''
                          ? props?.fields?.title
                          : pageInfo?.fields?.Title
                      }
                    />
                  </h1>
                )}
              </div>
              {!hideTranslationLink?.value && (
                <div className="hidden lg:block">
                  {alternateLanguageText && locale && (
                    <LinkWrapper
                      field={customLanguageLink}
                      ctaVariant="hyperlink"
                      ctaIcon="arrow-right"
                      locale={locale}
                    />
                  )}
                </div>
              )}
            </div>
            <RichTextWrapper
              className={Description()}
              field={
                props?.fields?.description?.value !== ''
                  ? props?.fields?.description
                  : pageInfo?.fields?.Description
              }
            />
          </div>
          {!hideTranslationLink?.value && (
            <div className="lg:hidden">
              {alternateLanguageText && locale && (
                <LinkWrapper
                  field={customLanguageLink}
                  ctaVariant="hyperlink"
                  ctaIcon="arrow-right"
                  locale={locale}
                />
              )}
            </div>
          )}
        </div>
      </div>
      <div className={ActionCardWrapper()}>
        <ActionCardSection actionCardsList={actionCardsList ? actionCardsList : []} />
      </div>
    </section>
  );
};

export default ConditionalHeader;

const TAILWIND_VARIANTS = tv({
  defaultVariants: {
    style: 'primary',
  },
  slots: {
    base: ['relative', 'theme-lightblue', 'bg-theme-bg', 'relative'],
    HeadingWrapper: [
      'lg:pt-[64px]',
      'lg:pb-[88px]',
      'lg:max-w-screen-xxl',
      'm-auto',
      'lg:px-[114px]',
      'px-10',
      'pt-[32px]',
      'flex',
      'lg:flex-row',
      'flex-col',
      'lg:justify-between',
    ],
    CategoryLabel: [
      'block',
      'font-centuryGothic',
      'text-16',
      'font-bold',
      'leading-[22px]',
      'text-gray-500',
      'mb-12',
    ],
    Title: [
      'font-centuryGothic',
      'lg:text-[48px]',
      'text-[40px]',
      'font-bold',
      'lg:leading-[58px]',
      'leading-[48px]',
      'text-gray-900',
      'mb-12',
    ],
    Description: [
      '[&>*]:font-centuryGothic',
      '[&>*]:!text-9',
      '[&>*]:!font-bold',
      '[&>*]:!leading-[25px]',
      '[&>*]:!text-gray-900',
      'lg:mb-0',
      'mb-10',
    ],
    ActionCardWrapper: [
      'lg:max-w-screen-xxl',
      'm-auto',
      'relative',
      'lg:-top-[35px]',
      'theme-lightblue',
      'lg:bg-transparent',
      'bg-theme-bg',
      'lg:px-[114px]',
      'px-10',
      'lg:py-0',
      'pt-[40px]',
      'pb-[32px]',
    ],
    cardSection: ['bg-white', 'rounded-md', 'shadow-default', 'border-gray-50'],
  },
  variants: {},
});
